/**
 * Service for Mollie payment integration
 * Handles subscriptions, payments, and billing
 */

import { getAuthHeaders } from './authService';

const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/payments`;

// Mollie configuration from environment
const MOLLIE_CONFIG = {
    apiKey: import.meta.env.VITE_MOLLIE_API_KEY,
    profileId: import.meta.env.VITE_MOLLIE_PROFILE_ID,
    redirectUrl: import.meta.env.VITE_MOLLIE_REDIRECT_URL,
    webhookUrl: import.meta.env.VITE_MOLLIE_WEBHOOK_URL,
};

/**
 * Helper function to handle API responses
 */
const handleResponse = async (response) => {
    const responseText = await response.text();
    console.log('Payment API Response status:', response.status);

    let result;
    try {
        result = JSON.parse(responseText);
    } catch (parseError) {
        console.error('Failed to parse JSON:', parseError);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }

    if (!response.ok) {
        const error = new Error(result.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = result;
        throw error;
    }

    return result;
};

/**
 * Create a new payment for a subscription
 * @param {string} peopleUuid - User's UUID
 * @param {string} planType - 'argent' or 'or'
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @returns {Promise<Object>} Payment details with checkout URL
 */
export const createPayment = async (peopleUuid, planType, billingPeriod = 'monthly') => {
    try {
        console.log('Creating payment:', { peopleUuid, planType, billingPeriod });

        const response = await fetch(`${API_BASE_URL}/create-payment`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                peopleUuid,
                planType,
                billingPeriod,
                // Pass Mollie config from frontend .env
                mollieApiKey: MOLLIE_CONFIG.apiKey,
                redirectUrl: MOLLIE_CONFIG.redirectUrl,
                webhookUrl: MOLLIE_CONFIG.webhookUrl,
            })
        });

        const result = await handleResponse(response);
        console.log('Payment created:', result);
        return result;
    } catch (error) {
        console.error('Error creating payment:', error);
        throw error;
    }
};

/**
 * Get user's current subscription status
 * @param {string} peopleUuid - User's UUID
 * @returns {Promise<Object>} Subscription details
 */
export const getSubscription = async (peopleUuid) => {
    try {
        console.log('Fetching subscription for:', peopleUuid);

        const response = await fetch(`${API_BASE_URL}/subscription/${peopleUuid}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        console.log('Subscription retrieved:', result);
        return result.data || result.DATA;
    } catch (error) {
        console.error('Error fetching subscription:', error);
        throw error;
    }
};

/**
 * Get user's payment history
 * @param {string} peopleUuid - User's UUID
 * @returns {Promise<Array>} List of payments
 */
export const getPaymentHistory = async (peopleUuid) => {
    try {
        console.log('Fetching payment history for:', peopleUuid);

        const response = await fetch(`${API_BASE_URL}/history/${peopleUuid}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        console.log('Payment history retrieved:', result);
        return result.data || result.DATA || [];
    } catch (error) {
        console.error('Error fetching payment history:', error);
        throw error;
    }
};

/**
 * Cancel user's subscription
 * @param {string} peopleUuid - User's UUID
 * @param {string} reason - Optional reason for cancellation
 * @returns {Promise<Object>} Cancellation result
 */
export const cancelSubscription = async (peopleUuid, reason = '') => {
    try {
        console.log('Cancelling subscription for:', peopleUuid);

        const response = await fetch(`${API_BASE_URL}/cancel-subscription`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                peopleUuid,
                mollieApiKey: MOLLIE_CONFIG.apiKey,
                reason,
            })
        });

        const result = await handleResponse(response);
        console.log('Subscription cancelled:', result);
        return result;
    } catch (error) {
        console.error('Error cancelling subscription:', error);
        throw error;
    }
};

/**
 * Get available subscription formules from the database
 * @returns {Promise<Array>} List of formules
 */
export const getFormules = async () => {
    try {
        console.log('Fetching formules from API');

        const response = await fetch(`${API_BASE_URL}/formules`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        console.log('Formules retrieved:', result);
        return result.data || result.DATA || [];
    } catch (error) {
        console.error('Error fetching formules:', error);
        throw error;
    }
};

/**
 * Upgrade subscription to a higher plan with prorata credit
 * @param {string} peopleUuid - User's UUID
 * @param {string} newPlanType - New plan type (e.g., 'or')
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @returns {Promise<Object>} Upgrade result with payment URL or immediate activation
 */
export const upgradeSubscription = async (peopleUuid, newPlanType, billingPeriod = 'monthly') => {
    try {
        console.log('Upgrading subscription:', { peopleUuid, newPlanType, billingPeriod });

        const response = await fetch(`${API_BASE_URL}/upgrade-subscription`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                peopleUuid,
                newPlanType,
                billingPeriod,
                mollieApiKey: MOLLIE_CONFIG.apiKey,
                redirectUrl: MOLLIE_CONFIG.redirectUrl,
                webhookUrl: MOLLIE_CONFIG.webhookUrl,
            })
        });

        const result = await handleResponse(response);
        console.log('Upgrade result:', result);
        return result;
    } catch (error) {
        console.error('Error upgrading subscription:', error);
        throw error;
    }
};

/**
 * Get available credits for a user
 * @param {string} peopleUuid - User's UUID
 * @returns {Promise<Object>} Credits info with total and list
 */
export const getCredits = async (peopleUuid) => {
    try {
        console.log('Fetching credits for:', peopleUuid);

        const response = await fetch(`${API_BASE_URL}/credits/${peopleUuid}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        console.log('Credits retrieved:', result);
        return {
            credits: result.data || result.DATA || [],
            totalCredit: result.totalCredit || result.TOTALCREDIT || 0
        };
    } catch (error) {
        console.error('Error fetching credits:', error);
        throw error;
    }
};

/**
 * Get subscription statistics (admin only)
 * @returns {Promise<Object>} Stats by plan
 */
export const getSubscriptionStats = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/subscription-stats`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return result.data || result.DATA || {};
    } catch (error) {
        console.error('Error fetching subscription stats:', error);
        throw error;
    }
};

/**
 * Convert API formule to frontend plan format
 * @param {Object} formule - Formule from API
 * @returns {Object} Plan in frontend format
 */
const convertFormuleToPlan = (formule) => {
    const features = formule.features || {};
    const featureList = [
        { text: features.contacts === null ? 'Contacts illimités' : `Jusqu'à ${features.contacts} contacts`, included: true },
        { text: 'Scan de cartes de visite', included: features.scanCards || false },
        { text: 'Fiche contact détaillée', included: features.detailedContact || false },
        { text: 'Création de groupes', included: features.groups || false },
        { text: 'Export des contacts', included: features.export || false },
        { text: 'Créer un évènement', included: features.events || false },
        { text: 'Objets à la vente', included: features.sales || false },
        { text: 'Proposer ses services', included: features.services || false },
        { text: 'Agenda intégré', included: features.agenda || false },
        { text: 'Gestion des disponibilités', included: features.availability || false },
    ];

    return {
        id: formule.code,
        name: formule.name,
        price: formule.priceMonthly,
        priceYearly: formule.priceYearly,
        period: '/mois',
        description: formule.description,
        features: featureList,
        cta: formule.priceMonthly === 0 ? 'Formule actuelle' : `Choisir ${formule.name}`,
        popular: formule.isPopular || false,
        maxContacts: formule.maxContacts,
    };
};

/**
 * Fetch and convert formules from API to PLANS format
 * @returns {Promise<Object>} Plans object keyed by code
 */
export const fetchPlans = async () => {
    try {
        const formules = await getFormules();
        const plans = {};
        formules.forEach(formule => {
            plans[formule.code] = convertFormuleToPlan(formule);
        });
        return plans;
    } catch (error) {
        console.error('Error fetching plans, using fallback:', error);
        return PLANS; // Fallback to hardcoded plans
    }
};

/**
 * Plan definitions with features (fallback/default)
 */
export const PLANS = {
    bronze: {
        id: 'bronze',
        name: 'Bronze',
        price: 0,
        priceYearly: 0,
        period: '/mois',
        description: 'Pour découvrir Cabaza',
        features: [
            { text: 'Jusqu\'à 5 contacts', included: true },
            { text: 'Scan de cartes de visite', included: true },
            { text: 'Fiche contact détaillée', included: true },
            { text: 'Création de groupes', included: false },
            { text: 'Contacts illimités', included: false },
            { text: 'Créer un évènement', included: false },
            { text: 'Objets à la vente', included: false },
            { text: 'Proposer ses services', included: false },
            { text: 'Agenda intégré', included: false },
            { text: 'Gestion des disponibilités', included: false },
        ],
        cta: 'Formule actuelle',
        popular: false,
    },
    argent: {
        id: 'argent',
        name: 'Argent',
        price: 4,
        priceYearly: 40,
        period: '/mois',
        description: 'Pour les professionnels',
        features: [
            { text: 'Contacts illimités', included: true },
            { text: 'Scan de cartes de visite', included: true },
            { text: 'Fiche contact détaillée', included: true },
            { text: 'Création de groupes', included: true },
            { text: 'Export des contacts', included: true },
            { text: 'Créer un évènement', included: true },
            { text: 'Objets à la vente', included: true },
            { text: 'Proposer ses services', included: false },
            { text: 'Agenda intégré', included: false },
            { text: 'Gestion des disponibilités', included: false },
        ],
        cta: 'Choisir Argent',
        popular: true,
    },
    or: {
        id: 'or',
        name: 'Or',
        price: 8,
        priceYearly: 80,
        period: '/mois',
        description: 'La solution complète',
        features: [
            { text: 'Contacts illimités', included: true },
            { text: 'Scan de cartes de visite', included: true },
            { text: 'Fiche contact détaillée', included: true },
            { text: 'Création de groupes', included: true },
            { text: 'Export des contacts', included: true },
            { text: 'Créer un évènement', included: true },
            { text: 'Objets à la vente', included: true },
            { text: 'Proposer ses services', included: true },
            { text: 'Agenda intégré', included: true },
            { text: 'Gestion des disponibilités', included: true },
        ],
        cta: 'Choisir Or',
        popular: false,
    }
};

/**
 * Get price for a plan based on billing period
 * @param {string} planId - Plan ID
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @returns {number} Price
 */
export const getPlanPrice = (planId, billingPeriod = 'monthly') => {
    const plan = PLANS[planId];
    if (!plan) return 0;
    return billingPeriod === 'yearly' ? plan.priceYearly : plan.price;
};

/**
 * Get savings percentage for yearly billing
 * @param {string} planId - Plan ID
 * @returns {number} Savings percentage
 */
export const getYearlySavings = (planId) => {
    const plan = PLANS[planId];
    if (!plan || plan.price === 0) return 0;
    const monthlyTotal = plan.price * 12;
    const savings = ((monthlyTotal - plan.priceYearly) / monthlyTotal) * 100;
    return Math.round(savings);
};

/**
 * Check if a feature is available for a given plan
 * @param {string} planType - 'bronze', 'argent', or 'or'
 * @param {string} featureName - Feature to check
 * @returns {boolean} Whether the feature is available
 */
export const hasFeature = (planType, featureName) => {
    const plan = PLANS[planType?.toLowerCase()];
    if (!plan) return false;

    const feature = plan.features.find(f => f.text.toLowerCase().includes(featureName.toLowerCase()));
    return feature?.included || false;
};

/**
 * Get the next upgrade plan
 * @param {string} currentPlan - Current plan type
 * @returns {Object|null} Next plan or null if already at max
 */
export const getUpgradePlan = (currentPlan) => {
    const planOrder = ['bronze', 'argent', 'or'];
    const currentIndex = planOrder.indexOf(currentPlan?.toLowerCase());

    if (currentIndex === -1 || currentIndex >= planOrder.length - 1) {
        return null;
    }

    return PLANS[planOrder[currentIndex + 1]];
};
